import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "../../primitives/Button";
import { cn } from "../../utils/cn";
import {
  buildMonthGrid,
  dateToBackendWeekday,
  formatDayTitleFr,
  formatMonthTitleFr,
  formatYmdLocal,
  WEEKDAY_HEADERS,
  WEEKDAY_PLURAL_FR,
} from "./bookingCalendarUtils";
import type { WeeklyRuleDTO } from "../../../api/bookings";

export type RuleDraft = Omit<WeeklyRuleDTO, "id">;

function defaultSlot(weekday: number, repeatWeekly: boolean, dateStr: string): RuleDraft {
  const base: RuleDraft = {
    weekday,
    start_time: "09:00",
    end_time: "17:00",
    slot_duration_minutes: 30,
    timezone: "America/Montreal",
  };
  if (!repeatWeekly) {
    return { ...base, specific_date: dateStr };
  }
  return base;
}

function dayHasAvailability(cell: Date, allRules: RuleDraft[]): boolean {
  const iso = formatYmdLocal(cell);
  const wd = dateToBackendWeekday(cell);
  return allRules.some(
    (r) =>
      (r.specific_date && r.specific_date === iso) || (!r.specific_date && r.weekday === wd)
  );
}

type Props = {
  rules: RuleDraft[];
  onRulesChange: (next: RuleDraft[]) => void;
  loading?: boolean;
};

export function BookingAvailabilityCalendar({ rules, onRulesChange, loading }: Props) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const [modalOpen, setModalOpen] = useState(false);
  const [modalWeekday, setModalWeekday] = useState<number | null>(null);
  const [modalContextDate, setModalContextDate] = useState<Date | null>(null);
  const [modalSlots, setModalSlots] = useState<RuleDraft[]>([]);
  /** true = répétition chaque semaine ; false = uniquement la date du calendrier */
  const [repeatWeekly, setRepeatWeekly] = useState(true);

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const openModalForDate = (d: Date) => {
    const wd = dateToBackendWeekday(d);
    const dateStr = formatYmdLocal(d);
    const oneOff = rules.filter((r) => r.specific_date === dateStr);
    const recurring = rules.filter((r) => !r.specific_date && r.weekday === wd);

    setModalWeekday(wd);
    setModalContextDate(d);

    if (oneOff.length > 0) {
      setRepeatWeekly(false);
      setModalSlots(oneOff.map((r) => ({ ...r })));
    } else if (recurring.length > 0) {
      setRepeatWeekly(true);
      setModalSlots(recurring.map((r) => ({ ...r })));
    } else {
      setRepeatWeekly(true);
      setModalSlots([defaultSlot(wd, true, dateStr)]);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalWeekday(null);
    setModalContextDate(null);
    setModalSlots([]);
    setRepeatWeekly(true);
  };

  const applyModal = () => {
    if (modalWeekday === null || !modalContextDate) return;
    const wd = modalWeekday;
    const dateStr = formatYmdLocal(modalContextDate);

    const cleaned = modalSlots
      .filter((s) => s.start_time && s.end_time)
      .map((s) => ({
        ...s,
        weekday: wd,
        slot_duration_minutes: s.slot_duration_minutes ?? 30,
        timezone: (s.timezone || "America/Montreal").trim() || "America/Montreal",
        specific_date: repeatWeekly ? null : dateStr,
      }));

    for (const s of cleaned) {
      if (s.start_time >= s.end_time) {
        window.alert("L’heure de fin doit être après l’heure de début pour chaque plage.");
        return;
      }
    }

    let others = rules;
    if (repeatWeekly) {
      others = rules.filter((r) => !(r.weekday === wd && !r.specific_date));
    } else {
      others = rules.filter((r) => r.specific_date !== dateStr);
    }
    onRulesChange([...others, ...cleaned]);
    closeModal();
  };

  const removeAllForCurrentScope = () => {
    if (modalWeekday === null || !modalContextDate) return;
    const wd = modalWeekday;
    const dateStr = formatYmdLocal(modalContextDate);
    const msg = repeatWeekly
      ? "Retirer toutes les plages récurrentes pour ce jour de la semaine ?"
      : "Retirer toutes les plages pour cette date uniquement ?";
    if (!window.confirm(msg)) return;
    if (repeatWeekly) {
      onRulesChange(rules.filter((r) => !(r.weekday === wd && !r.specific_date)));
    } else {
      onRulesChange(rules.filter((r) => r.specific_date !== dateStr));
    }
    closeModal();
  };

  const toggleRepeatWeekly = (next: boolean) => {
    if (modalWeekday === null || !modalContextDate) {
      setRepeatWeekly(next);
      return;
    }
    const wd = modalWeekday;
    const dateStr = formatYmdLocal(modalContextDate);

    if (next) {
      const rec = rules.filter((r) => !r.specific_date && r.weekday === wd);
      setModalSlots(rec.length > 0 ? rec.map((r) => ({ ...r })) : [defaultSlot(wd, true, dateStr)]);
    } else {
      const off = rules.filter((r) => r.specific_date === dateStr);
      setModalSlots(off.length > 0 ? off.map((r) => ({ ...r })) : [defaultSlot(wd, false, dateStr)]);
    }
    setRepeatWeekly(next);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpen) closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-surface p-8 animate-pulse">
        <div className="h-8 bg-border/60 rounded w-48 mb-6" />
        <div className="grid grid-cols-7 gap-2 h-64 bg-border/30 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-text">Calendrier des disponibilités</h2>
          <p className="text-xs text-muted mt-1">
            Cliquez sur un jour pour ajouter des plages (répétition chaque semaine ou une seule date). Les jours avec une
            plage affichent un indicateur coloré.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevMonth}
            className="p-2 rounded-md border border-border hover:bg-surface-hover"
            aria-label="Mois précédent"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-medium min-w-[10rem] text-center capitalize">
            {formatMonthTitleFr(viewYear, viewMonth)}
          </span>
          <button
            type="button"
            onClick={goNextMonth}
            className="p-2 rounded-md border border-border hover:bg-surface-hover"
            aria-label="Mois suivant"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface p-3 sm:p-4 shadow-sm">
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted mb-2">
          {WEEKDAY_HEADERS.map((h) => (
            <div key={h} className="py-1">
              {h}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) {
              return <div key={`empty-${idx}`} className="aspect-square min-h-[2.75rem]" />;
            }
            const has = dayHasAvailability(cell, rules);
            const isToday =
              cell.getDate() === now.getDate() &&
              cell.getMonth() === now.getMonth() &&
              cell.getFullYear() === now.getFullYear();
            return (
              <button
                key={`${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`}
                type="button"
                onClick={() => openModalForDate(cell)}
                className={cn(
                  "aspect-square min-h-[2.75rem] rounded-lg border text-sm flex flex-col items-center justify-center gap-0.5 transition",
                  "hover:border-accent hover:bg-accent/5 focus:outline-none focus:ring-2 focus:ring-accent/30",
                  isToday ? "border-accent/60 bg-accent/5 font-semibold" : "border-border/80 bg-background/50"
                )}
              >
                <span>{cell.getDate()}</span>
                {has && (
                  <span
                    className="h-2 w-2 rounded-full bg-emerald-500 shadow-sm ring-2 ring-emerald-500/25"
                    title="Au moins une plage définie"
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/25" />
            Jour avec disponibilité (récurrente ou date unique)
          </span>
        </div>
      </div>

      {modalOpen && modalWeekday !== null && modalContextDate && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[1px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
              <div className="min-w-0 flex-1">
                <h3 id="booking-modal-title" className="text-lg font-semibold text-text">
                  Plages pour ce jour
                </h3>
                <p className="text-sm text-muted mt-1">{formatDayTitleFr(modalContextDate)}</p>
                {repeatWeekly ? (
                  <p className="text-xs text-muted mt-1">
                    Récurrence : <strong>tous les {WEEKDAY_PLURAL_FR[modalWeekday]}</strong>
                  </p>
                ) : (
                  <p className="text-xs text-muted mt-1">
                    <strong>Date unique</strong> : cette date seulement ({formatYmdLocal(modalContextDate)}), sans
                    répétition les semaines suivantes.
                  </p>
                )}
                <label className="mt-3 flex items-start gap-2 cursor-pointer text-sm text-text">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-border"
                    checked={repeatWeekly}
                    onChange={(e) => toggleRepeatWeekly(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Répéter chaque semaine</span>
                    <span className="block text-xs text-muted font-normal">
                      Décochez pour une disponibilité ponctuelle (uniquement le jour sélectionné dans le calendrier).
                    </span>
                  </span>
                </label>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-md hover:bg-surface-hover text-muted shrink-0"
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {modalSlots.length === 0 && modalWeekday !== null && modalContextDate && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted mb-3">Aucune plage pour l’instant.</p>
                  <button
                    type="button"
                    className="text-sm font-medium text-accent hover:underline"
                    onClick={() =>
                      setModalSlots([
                        defaultSlot(modalWeekday, repeatWeekly, formatYmdLocal(modalContextDate)),
                      ])
                    }
                  >
                    + Ajouter une plage horaire
                  </button>
                </div>
              )}
              {modalSlots.map((slot, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-border p-3 space-y-3 bg-background/40"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted uppercase tracking-wide">Plage {i + 1}</span>
                    {modalSlots.length > 1 && (
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => setModalSlots(modalSlots.filter((_, j) => j !== i))}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted">Début</span>
                      <input
                        type="time"
                        className="rounded border border-border px-2 py-1.5 text-sm bg-background"
                        value={slot.start_time}
                        onChange={(e) => {
                          const next = [...modalSlots];
                          next[i] = { ...slot, start_time: e.target.value };
                          setModalSlots(next);
                        }}
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted">Fin</span>
                      <input
                        type="time"
                        className="rounded border border-border px-2 py-1.5 text-sm bg-background"
                        value={slot.end_time}
                        onChange={(e) => {
                          const next = [...modalSlots];
                          next[i] = { ...slot, end_time: e.target.value };
                          setModalSlots(next);
                        }}
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted">Durée des créneaux (min)</span>
                      <select
                        className="rounded border border-border px-2 py-1.5 text-sm bg-background"
                        value={slot.slot_duration_minutes ?? 30}
                        onChange={(e) => {
                          const next = [...modalSlots];
                          next[i] = { ...slot, slot_duration_minutes: Number(e.target.value) };
                          setModalSlots(next);
                        }}
                      >
                        {[15, 30, 45, 60].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted">Fuseau horaire (IANA)</span>
                      <input
                        className="rounded border border-border px-2 py-1.5 text-sm bg-background"
                        value={slot.timezone ?? "America/Montreal"}
                        onChange={(e) => {
                          const next = [...modalSlots];
                          next[i] = { ...slot, timezone: e.target.value };
                          setModalSlots(next);
                        }}
                        placeholder="America/Montreal"
                      />
                    </label>
                  </div>
                </div>
              ))}

              {modalSlots.length > 0 && modalWeekday !== null && modalContextDate && (
                <button
                  type="button"
                  className="w-full py-2 text-sm font-medium rounded-lg border border-dashed border-border hover:border-accent hover:bg-accent/5 text-accent"
                  onClick={() =>
                    setModalSlots([
                      ...modalSlots,
                      defaultSlot(modalWeekday, repeatWeekly, formatYmdLocal(modalContextDate)),
                    ])
                  }
                >
                  + Ajouter une autre plage le même jour
                </button>
              )}
            </div>

            <div className="p-4 border-t border-border flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button label="Annuler" intent="ghost" size="md" onClick={closeModal} />
              <Button
                label={repeatWeekly ? "Tout supprimer (récurrent)" : "Tout supprimer (cette date)"}
                intent="danger"
                size="md"
                onClick={removeAllForCurrentScope}
              />
              <Button label="Enregistrer les plages" intent="primary" size="md" onClick={applyModal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
